import * as UUID from 'uuid'
import * as React from 'react'
import { CSSProperties, FunctionComponent, PropsWithChildren, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import ErrorBoundary from '../components/Diagnostics/ErrorBoundary'
import Router from './Router'
import { FileProvider } from '../context/FileContext'
import { MessageProvider } from '../context/MessageContext'
import MainMenu from '../components/MainMenu'
import Errors from '../components/Diagnostics/Errors'
import Commands from './Commands'
import { MemoryRouter } from 'react-router-dom'
import axios from '../helpers/axiosHelper'
import AxiosContext from '../context/AxiosContext'
import { Route } from 'react-router'
import { Stack, StackItem } from '@fluentui/react'
import {
    FluentProvider,
    makeStyles,
    mergeClasses,
    MessageBar,
    MessageBarBody,
    shorthands,
    Spinner,
    tokens,
    webDarkTheme,
    webLightTheme,
} from '@fluentui/react-components'
import { ToastContainer } from 'react-toastify'
import Version from '../components/Diagnostics/Version'
import Support from '../v2/components/Diagnostics/Support'
import FileLink from '../components/Quicklink/FileLink'
import { GenerationQueueProvider } from '../context/QueueContext'
import { MollieProvider } from '../context/MollieContext'
import { useTranslation } from 'react-i18next'
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js'
import { appInsights, appInsightsReact } from '../helpers/appInsights'
import { I18NProvider } from '../context/I18NContext'
import MailboxAvatar from '../components/Diagnostics/MailboxAvatar'
import AuthorizationContext, { AuthorizationProvider } from '../context/AuthorizationContext'
import { GlobalProvider } from '../context/GlobalContext'
import { AzureWebSocketProvider } from '../context/AzureWebSocketContext'
import { OfficeEventHandlerProvider } from '../context/OfficeEventHandlerContext'
import useUser from '../hooks/useUser'
import SelectHost from '../components/Authentication/SelectHost'
import useTokenInjector from '../hooks/identity/useTokenInjector'
import useTokenErrorHandler from '../hooks/identity/useTokenErrorHandler'
import { AxiosError, AxiosResponse } from 'axios'
import { useMount } from '@fluentui/react-hooks'
import useAxios from '../hooks/useAxios'
import TimeSuggestionCounter from '../v2/components/Footer/TimeSuggestionCounter'
import Loading from '../components/Loading'
import FirstVisit from './Intro/FirstVisit'
import useLocalStorage from '../hooks/useLocalStorage'
import useResolvedTheme from '../hooks/useResolvedTheme'
import { PopupProvider } from '../context/PopupContext'
import useViewport, { Viewport } from '../hooks/useViewport'
import { createGraphRetryHandler } from '../helpers/graphRetry'
import ConnectivityContext, { ConnectivityProvider } from '../context/ConnectivityContext'

export { appInsights, appInsightsReact } from '../helpers/appInsights'

export const toastContainerId = 'toastContainerId'

export const useApplicationStyles = makeStyles({
    wrapper: {
        backgroundColor: '#eff6fc',
        backgroundImage: "url('../images/hammock-bg.png')",
        backgroundSize: '1200px 600px',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        minHeight: '100%',
    },
    // Applied on top of `wrapper` when the resolved theme is dark. Keyed off useResolvedTheme
    // instead of `@media (prefers-color-scheme: dark)` so the manual toggle works too.
    wrapperDark: {
        backgroundColor: '#000000',
        backgroundImage: 'unset',
    },
    container: {
        minWidth: 0,
        maxWidth: '1280px',
        backgroundColor: tokens.colorNeutralBackgroundDisabled,
        boxShadow: tokens.shadow16,

        '> :not(:first-child)': {
            marginTop: tokens.spacingVerticalS,
        },
    },
    accordionContent: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    sticky: {
        position: 'sticky',
        zIndex: 10000,
        top: 'var(--sticky-top)',
    },
    content: {
        display: 'flex',

        marginLeft: tokens.spacingHorizontalS,
        marginRight: tokens.spacingHorizontalS,

        '@media screen and (max-width: 400px)': {
            marginLeft: tokens.spacingHorizontalXS,
            marginRight: tokens.spacingHorizontalXS,
        },
    },
    subMenu: {
        position: 'sticky',
        top: '44px',
        zIndex: 7,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    actionItems: {
        position: 'sticky',
        top: '88px',
        zIndex: 8,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    footer: {
        position: 'sticky',
        bottom: 0,
        zIndex: 1000,
        height: '40px',
        backgroundColor: tokens.colorBrandBackground2,
    },
    footerSection: {
        display: 'flex',
        gap: tokens.spacingVerticalS,
    },
    minHeight: {
        minHeight: 'fit-content',
    },

    thinTextButton: {
        fontWeight: tokens.fontWeightRegular,
    },
    displayFlex: {
        display: 'flex',
    },
    flexWrap: {
        flexWrap: 'wrap',
    },
    flexAlignItemsStart: {
        alignItems: 'start',
    },
    flexColumn: {
        display: 'flex',
        flexDirection: 'column',
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
    },
    gapS: {
        gap: tokens.spacingVerticalS,
    },
    selfCenter: {
        alignSelf: 'center',
    },
    selfStart: {
        alignSelf: 'start',
        justifyContent: 'start',
    },
    verticalAlign: {
        verticalAlign: 'middle',
    },
    alignItems: {
        alignItems: 'center',
    },
    alignSelf: {
        alignSelf: 'center',
    },
    spaceBetween: {
        display: 'flex',
        gap: tokens.spacingVerticalS,
        justifyContent: 'space-between',
    },
    textNoWrapOverflowHidden: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    overflowXHidden: {
        overflowX: 'hidden',
        maxWidth: 'inherit',
    },
    width100: {
        width: '100%',
    },
    height100: {
        height: '100%',
    },
})

const Template: FunctionComponent<PropsWithChildren<{ authenticated: boolean }>> = props => {
    const styles = useApplicationStyles()
    const { showOnlineInfo, showOfflineWarning } = useContext(ConnectivityContext)
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()
    const { resolvedTheme } = useResolvedTheme()

    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    return (
        <Stack
            id={'wrapper'}
            aria-label={'wrapper'}
            horizontal
            horizontalAlign="center"
            className={mergeClasses(styles.wrapper, resolvedTheme === 'dark' && styles.wrapperDark)}
            style={
                {
                    '--sticky-top': '0px',
                } as CSSProperties
            }
        >
            <Stack id={'contentcontainer'} aria-label={'contentcontainer'} grow className={styles.container}>
                {props.authenticated && (
                    <Stack aria-label={'header'} className={styles.sticky}>
                        <MainMenu />
                        {showOfflineWarning && (
                            <MessageBar intent={'warning'}>
                                <MessageBarBody>{translate('base.overview.internet.offline')}</MessageBarBody>
                            </MessageBar>
                        )}
                        {showOnlineInfo && (
                            <MessageBar intent={'success'}>
                                <MessageBarBody>{translate('base.overview.internet.online')}</MessageBarBody>
                            </MessageBar>
                        )}
                    </Stack>
                )}
                <Errors />
                <Stack
                    aria-label={'content'}
                    grow
                    className={styles.content}
                    style={
                        {
                            '--sticky-top': smallMenu ? '64px' : '76px',
                        } as CSSProperties
                    }
                >
                    {props.children}
                    {props.authenticated && <Commands />}
                    <ToastContainer position="bottom-right" limit={5} stacked containerId={toastContainerId} />
                </Stack>
                <Stack aria-label={'footer'} horizontal wrap horizontalAlign="space-between" className={styles.footer}>
                    <StackItem className={styles.footerSection}>
                        {props.authenticated && <TimeSuggestionCounter />}
                        <FileLink />
                    </StackItem>
                    <StackItem className={styles.footerSection}>
                        <Support />
                        {props.authenticated && <MailboxAvatar />}
                        <Version />
                    </StackItem>
                </Stack>
            </Stack>
        </Stack>
    )
}

const AuthenticatedContainer: FunctionComponent<PropsWithChildren> = () => {
    return (
        <MemoryRouter initialEntries={[window.location.hash.substring(1)]}>
            <GlobalProvider>
                <GenerationQueueProvider>
                    <FileProvider>
                        <MessageProvider>
                            <MollieProvider>
                                <PopupProvider>
                                    <AzureWebSocketProvider>
                                        <OfficeEventHandlerProvider>
                                            <Template authenticated={true}>
                                                <Route path="/" component={Router} />
                                            </Template>
                                        </OfficeEventHandlerProvider>
                                    </AzureWebSocketProvider>
                                </PopupProvider>
                            </MollieProvider>
                        </MessageProvider>
                    </FileProvider>
                </GenerationQueueProvider>
            </GlobalProvider>
        </MemoryRouter>
    )
}

const Container: FunctionComponent<PropsWithChildren> = () => {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const tokenInjector = useTokenInjector()
    const tokenErrorHandler = useTokenErrorHandler()

    const { identified, authenticated, host: currentHost } = useContext(AuthorizationContext)

    useMount(() => {
        axios.interceptors.request.use(tokenInjector, error => Promise.reject(error))
        const graphRetryHandler = createGraphRetryHandler(axios)
        axios.interceptors.response.use((value: AxiosResponse) => value, graphRetryHandler)
        axios.interceptors.response.use(
            (value: AxiosResponse) => value,
            (error: AxiosError) => new Promise<AxiosResponse>(tokenErrorHandler(error)),
        )
    })

    useEffect(() => {
        // Route to the correct base path so that each user gets the correct version served.
        if (currentHost?.ClientPath && window.location.pathname !== currentHost?.ClientPath) {
            // Redirect if we're not in the correct environment.
            // window.location.href = host.clientPath + window.location.search + window.location.hash
            const newLocation = currentHost?.ClientPath + window.location.search + (window.location.hash || '#') + window.location.pathname
            window.location.replace(newLocation)
        }
    }, [currentHost])

    const {
        loaded: loadedFirstVisit,
        values: firstVisit,
        setValues: setFirstVisit,
    } = useLocalStorage<boolean, boolean>({
        key: 'firstVisit',
        defaultValue: true,
        fromJson: values => values,
        toJson: values => values,
    })

    //Suppress the first visit carousel when we're in a production host.
    useEffect(() => {
        if (!['dev', 'test'].includes(process.env.BOOTSTRAP_ENV)) {
            setFirstVisit(false)
        }
    }, [firstVisit])

    if (!authenticated) {
        return (
            <Template authenticated={false}>
                {currentHost?.ClientPath && window.location.pathname !== currentHost?.ClientPath && (
                    <Spinner label={translate('authentication.host.redirect')} />
                )}
                {!loadedFirstVisit && <Loading />}
                {loadedFirstVisit && firstVisit && (
                    <FirstVisit
                        setVisited={() => {
                            setFirstVisit(false)
                        }}
                    />
                )}
                {loadedFirstVisit && !firstVisit && !identified && <Loading />}
                {loadedFirstVisit && !firstVisit && identified && !currentHost && <SelectHost />}
                {loadedFirstVisit && !firstVisit && identified && currentHost && !authenticated && <Loading />}
            </Template>
        )
    }

    return <AuthenticatedContainer />
}

const InsightsContainer: FunctionComponent<PropsWithChildren> = props => {
    const { i18n } = useTranslation()
    const { currentUser } = useUser()

    const { authenticated, host } = useContext(AuthorizationContext)

    useEffect(() => {
        if (authenticated && currentUser) {
            appInsights.setAuthenticatedUserContext(currentUser?.id, host.RowKey, true)
            appInsights.trackEvent({
                name: 'i18n',
                properties: {
                    language: i18n.language,
                },
            })
        }
    }, [authenticated, currentUser])

    return <AppInsightsContext.Provider value={appInsightsReact}>{props.children}</AppInsightsContext.Provider>
}

export default function App(): ReactElement {
    const [currentVersion] = useState(process.env.COMMITHASH)
    const [currentVersionTimeout, setCurrentVersionTimeout] = useState<NodeJS.Timeout>(undefined)

    const { resolvedTheme } = useResolvedTheme()

    const fetchNewVersion = () => {
        const commitHashUrl = window.location.origin + (window.location.pathname + '/COMMITHASH').replaceAll('//COMMITHASH', '/COMMITHASH')

        fetch(commitHashUrl, { cache: 'no-cache', headers: { 'Cache-Control': 'no-cache' } })
            .then(response => {
                if (response.ok) {
                    return response.text()
                } else {
                    console.log('Could not load /COMMITHASH: status: ' + response.status)
                    return currentVersion
                }
            })
            .then(response => (UUID.validate(response) ? response : currentVersion))
            .then((newVersion: string) => {
                console.log(`Update check currentVersion=${currentVersion} , newVersion=${newVersion}`)
                if (currentVersion !== newVersion) {
                    window.location.reload()
                } else {
                    setCurrentVersionTimeout(setTimeout(() => fetchNewVersion(), 3600000))
                }
            })
            .catch(error => {
                if (error?.message?.includes('ERR_NETWORK') || error?.name?.includes('ERR_NETWORK')) {
                    setCurrentVersionTimeout(setTimeout(() => fetchNewVersion(), 300000))
                }
            })
    }

    useEffect(() => {
        fetchNewVersion()

        return () => currentVersionTimeout && clearTimeout(currentVersionTimeout)
    }, [])

    return (
        <FluentProvider theme={resolvedTheme === 'dark' ? webDarkTheme : webLightTheme}>
            <ConnectivityProvider>
                <I18NProvider>
                    <ErrorBoundary>
                        <AxiosContext.Provider value={axios}>
                            <AuthorizationProvider>
                                <InsightsContainer>
                                    <Container />
                                </InsightsContainer>
                            </AuthorizationProvider>
                        </AxiosContext.Provider>
                    </ErrorBoundary>
                </I18NProvider>
            </ConnectivityProvider>
        </FluentProvider>
    )
}
